import { Injectable, inject } from '@angular/core';
import { jsPDF } from 'jspdf';
import { TranslateService } from '@ngx-translate/core';
import { ActivityLogEvent, ActivityLogStore } from '../../state/activity-log.repository';
import { aggregateAttendance } from '../../state/activity-log.aggregate';
import { UserSettingsStore } from '../../state/user-settings.repository';
import { downloadFile } from '../../shared/helpers/download-file';

type RosterEntry = {
  userId: string;
  name: string;
  firstJoin: Date;
  lastLeft: Date;
  stillInRoom: boolean;
};

/**
 * Produces attendance documents (PDF or Markdown) from the activity log.
 * One row per unique user that ever joined the current room, sorted by
 * first-join time. "Left" is either the user's final `left` event time or
 * — if their latest event is `joined` — the time the export was generated,
 * with a "still in room" annotation.
 *
 * Reads `ActivityLogStore` directly (excludes breakout-scope events since
 * those track sub-room movement, not main meeting attendance) and runs the
 * shared `aggregateAttendance` aggregator to collapse events into rows.
 */
@Injectable({ providedIn: 'root' })
export class AttendanceExportService {
  private _store = inject(ActivityLogStore);
  private _userSettings = inject(UserSettingsStore);
  private _translate = inject(TranslateService);

  exportAsPdf(): void {
    const roomName = this._userSettings.value.roomName || 'room';
    const generatedAt = new Date();
    const roster = this._buildRoster(generatedAt);

    const doc = new jsPDF({ unit: 'pt', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const marginX = 48;
    const marginBottom = 48;
    let y = 64;

    // Title.
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text(this._translate.instant('Attendance — [room]', { room: roomName }), marginX, y);
    y += 22;

    // Subtitle (generation timestamp).
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(120);
    doc.text(
      this._translate.instant('Generated [time]', {
        time: this._formatIso(generatedAt),
      }),
      marginX,
      y
    );
    doc.setTextColor(0);
    y += 28;

    // Column layout.
    const colName = marginX;
    const colJoined = marginX + 200;
    const colLeft = marginX + 340;

    // Header row.
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text(this._translate.instant('Name'), colName, y);
    doc.text(this._translate.instant('Joined'), colJoined, y);
    doc.text(this._translate.instant('Left'), colLeft, y);
    y += 8;
    doc.setDrawColor(180);
    doc.line(marginX, y, pageWidth - marginX, y);
    y += 16;

    // Body rows.
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    if (roster.length === 0) {
      doc.setTextColor(120);
      doc.text(this._translate.instant('No participants recorded.'), colName, y);
      doc.setTextColor(0);
    } else {
      const rowHeight = 18;
      for (const entry of roster) {
        if (y + rowHeight > pageHeight - marginBottom) {
          doc.addPage();
          y = 64;
        }
        doc.text(entry.name, colName, y);
        doc.text(this._formatTime(entry.firstJoin), colJoined, y);
        doc.text(this._formatLeftCell(entry, generatedAt), colLeft, y);
        y += rowHeight;
      }
    }

    doc.save(this._buildFilename(roomName, generatedAt, 'pdf'));
  }

  exportAsMarkdown(): void {
    const roomName = this._userSettings.value.roomName || 'room';
    const generatedAt = new Date();
    const roster = this._buildRoster(generatedAt);

    const lines: string[] = [];
    lines.push(`# ${this._translate.instant('Attendance — [room]', { room: roomName })}`);
    lines.push('');
    lines.push(
      this._translate.instant('Generated [time]', {
        time: this._formatIso(generatedAt),
      })
    );
    lines.push('');
    lines.push(
      `| ${this._translate.instant('Name')} | ${this._translate.instant('Joined')} | ${this._translate.instant('Left')} |`
    );
    lines.push('| --- | --- | --- |');

    if (roster.length === 0) {
      lines.push(`| _${this._translate.instant('No participants recorded.')}_ |  |  |`);
    } else {
      for (const entry of roster) {
        const name = this._escapePipe(entry.name);
        const joined = this._formatIso(entry.firstJoin);
        const left = this._formatLeftCell(entry, generatedAt);
        lines.push(`| ${name} | ${joined} | ${this._escapePipe(left)} |`);
      }
    }

    const blob = new Blob([lines.join('\n') + '\n'], { type: 'text/markdown' });
    downloadFile(blob, this._buildFilename(roomName, generatedAt, 'md'));
  }

  private _buildRoster(generatedAt: Date): RosterEntry[] {
    const roomId = this._userSettings.value.roomId;
    const state = this._store.store.getValue();
    const events: ActivityLogEvent[] = [];
    for (const id of state.ids as string[]) {
      const event = state.entities[id] as ActivityLogEvent | undefined;
      if (!event) continue;
      if (event.roomId !== roomId) continue;
      if (event.scope !== 'room') continue;
      events.push(event);
    }

    return aggregateAttendance(events).map((row) => ({
      userId: row.userId,
      name: row.name,
      firstJoin: row.firstJoin,
      lastLeft: row.lastLeft ?? generatedAt,
      stillInRoom: row.lastLeft === null,
    }));
  }

  private _formatLeftCell(entry: RosterEntry, generatedAt: Date): string {
    if (entry.stillInRoom) {
      return this._translate.instant('still in room (as of [time])', {
        time: this._formatTime(generatedAt),
      });
    }
    return this._formatIso(entry.lastLeft);
  }

  private _formatIso(date: Date): string {
    // `YYYY-MM-DD HH:MM` — readable in tables, no milliseconds clutter.
    const pad = (n: number) => n.toString().padStart(2, '0');
    return (
      `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ` +
      `${pad(date.getHours())}:${pad(date.getMinutes())}`
    );
  }

  private _formatTime(date: Date): string {
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
  }

  // Matches the whiteboard precedent at
  // `features/whiteboard/whiteboard.component.ts:97-105`. `getMonth()` is
  // 0-indexed there too — preserved for filename-format consistency.
  private _buildFilename(roomName: string, now: Date, ext: 'pdf' | 'md'): string {
    return (
      `${now.getFullYear()}.${now.getMonth()}.${now.getDate()}_` +
      `${now.getHours()}.${now.getMinutes()}_` +
      `${roomName}_attendance.${ext}`
    );
  }

  private _escapePipe(text: string): string {
    return text.replace(/\|/g, '\\|');
  }
}
